import { Box, Button, Collapse, Group, Stack, Table, Text } from "@mantine/core"
import { useDisclosure } from "@mantine/hooks"
import { CaretDownIcon, CaretUpIcon, EyeIcon, LinkSimpleIcon, MapTrifoldIcon, PhoneIcon, WarningIcon } from "@phosphor-icons/react"
import dayjs from "dayjs"
import { useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"
import useSWR from "swr"
import { matchesBusinessName } from "@dehub/theme"
import { DeliveryApi, VehicleApi } from "../../apis"
import { IDeliveryNotePlus } from "../../models/DeliveryNote"
import { formatReceiverAddress } from "../../utils/format-branch-address"
import { message } from "../../utils/message"
import { renderBusinessName } from "../../utils/render-business-name"
import { renderFullName } from "../../utils/render-name"
import { DeliveryRowAction } from "../delivery/action-controls"
import { ACTIVE_DN_STATUSES, useDeliveryActions } from "../delivery/delivery-actions"
import { Dot, FrameFooter, TablePager, Tone } from "../delivery/atoms"
import { translateBackendError } from "../delivery/translate-error"
import { ReportFailedDialog } from "../failed-deliveries/ReportFailedDialog"
import { computeSla } from "../pending-deliveries/atoms"
import { CustomTable, ITableRef } from "../table/customTable"
import { ActiveDeliveriesMap } from "./ActiveDeliveriesMap"
import { ActiveFiltersToolbar, ActiveQuickFilter } from "./ActiveFiltersToolbar"
import { ActiveStatsCards } from "./ActiveStatsCards"
import { DnStatusBadge, GpsStatusCell, deriveCapacityState, VehicleCell } from "./atoms"
import { useOrderTypeFilter, useStatusFilter } from "./hooks"
import type { OrderTypeFilterValue, StatusFilterValue } from "./hooks"
import classes from "./active.module.css"

const STATUS_TAB_VALUES = ["ALL", "ASSIGNED", "PENDING_HANDOVER", "LOADED", "DELIVERING"] as const

const ORDER_TYPE_VALUES: Array<{ value: OrderTypeFilterValue; key: string }> = [
  { value: null, key: "ALL" },
  { value: "REGULAR", key: "REGULAR" },
  { value: "CONSIGNMENT_INITIAL", key: "CONSIGNMENT_INITIAL" },
  { value: "CONSIGNMENT_REPLENISHMENT", key: "CONSIGNMENT_REPLENISHMENT" },
  { value: "CONSIGNMENT_RETURN", key: "CONSIGNMENT_RETURN" },
  { value: "CONSIGNMENT_TRANSFER", key: "CONSIGNMENT_TRANSFER" },
]

const VIP_AMOUNT_THRESHOLD = 10_000_000
/** BE active-stats definitions.liveWindowMinutes; used until the first stats response arrives. */
const DEFAULT_LIVE_WINDOW_MIN = 30

const valueToTab = (v: StatusFilterValue): string => v ?? "ALL"
const tabToValue = (t: string | null): StatusFilterValue => (!t || t === "ALL" ? null : (t as StatusFilterValue))

// The proposal wins when there is one: a postponed delivery judged against its original date would
// show as overdue the moment it is postponed.
function effectiveDate(dn: IDeliveryNotePlus): string | null | undefined {
  return dn.rescheduledDeliveryDate ?? dn.deliveryDate
}

function priorityOf(dn: IDeliveryNotePlus): "VIP" | "URGENT" | "NORMAL" {
  if ((dn.totalAmount ?? 0) >= VIP_AMOUNT_THRESHOLD) return "VIP"
  const due = effectiveDate(dn)
  if (due && dayjs(due).diff(dayjs(), "hour") < 24) return "URGENT"
  return "NORMAL"
}

/** O107: live = DELIVERING with a GPS fix inside the window. LOADED is never live. */
function isLiveDn(dn: IDeliveryNotePlus, windowMin: number): boolean {
  if (dn.deliveryNoteStatus !== "DELIVERING" || !dn.currentRecordedAt) return false
  return dayjs().diff(dayjs(dn.currentRecordedAt), "minute") <= windowMin
}

const PRIORITY_TONE: Record<string, Tone> = { VIP: "red", URGENT: "amber", NORMAL: "grey" }

/** Идэвхтэй tab (canvas Ord_DeliveryTabsA). */
export function ActiveDeliveries() {
  const { t } = useTranslation("delivery")
  const navigate = useNavigate()
  const { stateOf } = useDeliveryActions()
  const statusFilter = useStatusFilter()
  const orderTypeFilter = useOrderTypeFilter()
  const [mapOpened, { toggle: toggleMap }] = useDisclosure(false)
  const [allRows, setAllRows] = useState<IDeliveryNotePlus[]>([])
  const [total, setTotal] = useState(0)
  const tableRef = useRef<ITableRef>(null)
  const [quick, setQuick] = useState<ActiveQuickFilter>("ALL")
  const [search, setSearch] = useState("")
  const [reportDn, setReportDn] = useState<IDeliveryNotePlus | null>(null)

  // O105/O106: KPI and tab counts come from one server-wide call, not the 20 rows on this page.
  const { data: stats, mutate: refreshStats } = useSWR(
    ["swr.delivery.active-stats", orderTypeFilter.value],
    () => DeliveryApi.activeStats({ orderType: orderTypeFilter.value }),
    { revalidateOnFocus: false, refreshInterval: 60_000 },
  )
  const liveWindow = stats?.definitions.liveWindowMinutes ?? DEFAULT_LIVE_WINDOW_MIN

  const { data: vehicleData } = useSWR(
    "active.vehicles",
    () => VehicleApi.list({ filter: { isActive: true }, offset: { limit: 100, skip: 0 } }),
    { revalidateOnFocus: false },
  )

  const onClearFilters = () => {
    setQuick("ALL")
    setSearch("")
    orderTypeFilter.setValue(null)
    statusFilter.setValue(null)
  }

  const handleCopyTrackLink = async (dnId: string) => {
    try {
      const link = await DeliveryApi.getPublicTrackLink(dnId)
      await navigator.clipboard.writeText(`${window.location.origin}${link.path}`)
      message.success(t("active.trackLinkCopied"))
    } catch (err) {
      message.error(translateBackendError(err))
    }
  }

  // O106: «Бүгд» sends the 4 active statuses so PENDING_HANDOVER is listed too (BE default omits it).
  const filters = useMemo(() => {
    const next: { deliveryNoteStatus: string | string[]; orderType?: string } = {
      deliveryNoteStatus: statusFilter.value ?? [...ACTIVE_DN_STATUSES],
    }
    if (orderTypeFilter.value) next.orderType = orderTypeFilter.value
    return next
  }, [statusFilter.value, orderTypeFilter.value])

  const statusOptions = useMemo(
    () =>
      STATUS_TAB_VALUES.map((value) => {
        const n = value === "ALL" ? stats?.active : stats?.byStatus[value]
        return { value, label: `${t(`active.statusTab.${value}`)} (${n ?? "-"})` }
      }),
    [stats, t],
  )

  const vehicleCounts = useMemo(() => {
    const rows = vehicleData?.rows ?? []
    return {
      ready: rows.filter((v) => v.isActive).length,
      total: vehicleData?.count ?? rows.length,
      onRoute: 0,
      maintenance: rows.filter((v) => !v.isActive).length,
    }
  }, [vehicleData])

  // Quick chips narrow the current page only (no server filter yet, BE contract §5); counts are server-wide.
  const filterRowClient = (rows: IDeliveryNotePlus[]): IDeliveryNotePlus[] => {
    let r = rows
    const needle = search.trim().toLowerCase()
    if (needle) {
      r = r.filter(
        (dn) =>
          [dn.refCode, dn.salesCode, dn.receiverBranch?.name].some((val) => val?.toLowerCase().includes(needle)) ||
          matchesBusinessName(dn.receiverBusiness, search),
      )
    }
    if (quick === "VIP_URGENT") r = r.filter((dn) => priorityOf(dn) !== "NORMAL")
    else if (quick === "SLA_RISK") r = r.filter((dn) => computeSla(effectiveDate(dn), dn.createdAt).severity === "CRITICAL")
    else if (quick === "UNASSIGNED") r = r.filter((dn) => !dn.staffId)
    return r
  }

  const lastSeen = (dn: IDeliveryNotePlus): string | null => {
    if (!dn.currentRecordedAt) return null
    const minutes = dayjs().diff(dayjs(dn.currentRecordedAt), "minute")
    return minutes <= liveWindow
      ? t("active.minAgo", { count: Math.max(0, minutes) })
      : t("manage.active.lastSeen", { date: dayjs(dn.currentRecordedAt).format("MM-DD") })
  }

  return (
    <Stack gap="md">
      <ActiveStatsCards stats={stats} availableVehicles={vehicleCounts} />

      <Box>
        <Group justify="space-between" align="center">
          <Group gap="xs">
            <MapTrifoldIcon size={18} />
            <Text fw={600} fz="var(--nw-fz-body)">
              {t("active.map")}
            </Text>
            <Dot tone="green" />
            <Text fz="var(--nw-fz-body)">{t("active.liveDot")}</Text>
          </Group>
          <Button
            variant="default"
            leftSection={mapOpened ? <CaretUpIcon size={16} /> : <CaretDownIcon size={16} />}
            onClick={toggleMap}
          >
            {mapOpened ? t("active.close") : t("active.open")}
          </Button>
        </Group>
        <Collapse expanded={mapOpened}>
          <Box mt="xs">
            <ActiveDeliveriesMap />
          </Box>
        </Collapse>
      </Box>

      <ActiveFiltersToolbar
        status={valueToTab(statusFilter.value)}
        onStatusChange={(next) => statusFilter.setValue(tabToValue(next))}
        statusOptions={statusOptions}
        orderType={orderTypeFilter.value}
        onOrderTypeChange={(next) => orderTypeFilter.setValue(next as OrderTypeFilterValue)}
        orderTypeOptions={ORDER_TYPE_VALUES.map((o) => ({ value: o.value, label: t(`active.orderType.${o.key}`) }))}
        quick={quick}
        onQuickChange={setQuick}
        counts={{
          all: stats?.active ?? total,
          vipUrgent: stats?.vipUrgent ?? 0,
          slaRisk: stats?.slaRisk ?? 0,
          unassigned: stats?.unassigned ?? 0,
        }}
        search={search}
        onSearchChange={setSearch}
        onClear={onClearFilters}
      />

      <Box className={classes.frame}>
        <CustomTable
          minWidth={1110}
          ref={tableRef}
          name="active-deliveries"
          colCount={9}
          layout="fixed"
          filters={filters}
          loadData={async (filter) => {
            const result = await DeliveryApi.list({
              offset: filter?.offset ?? { page: 1, limit: 20 },
              filter: filter?.filter ?? {},
              order: ["updatedAt", "DESC"],
            })
            setAllRows((result as { rows?: IDeliveryNotePlus[] })?.rows ?? [])
            return result
          }}
          onResult={(res: { count?: number }) => setTotal(res?.count ?? 0)}
          thead={
            <Table.Thead>
              <Table.Tr>
                <Table.Th w={148}>{t("active.colAction")}</Table.Th>
                <Table.Th w={136}>{t("active.colNumber")}</Table.Th>
                <Table.Th>{t("active.colCustomer")}</Table.Th>
                <Table.Th className={classes.colItems}>{t("active.colItems")}</Table.Th>
                <Table.Th w={130}>{t("active.colVehicle")}</Table.Th>
                <Table.Th w={116}>{t("active.colDriver")}</Table.Th>
                <Table.Th w={130}>{t("active.colGps")}</Table.Th>
                <Table.Th className={classes.colStatus}>{t("manage.active.colStatus")}</Table.Th>
                <Table.Th className={classes.colTime}>{t("active.colTime")}</Table.Th>
              </Table.Tr>
            </Table.Thead>
          }
          tbody={(rows: IDeliveryNotePlus[]) => (
            <Table.Tbody>
              {filterRowClient(rows).map((dn) => {
                const id = dn.id ?? ""
                const priority = priorityOf(dn)
                const sla = computeSla(effectiveDate(dn), dn.createdAt)
                const route = dn.route ?? null
                const dnSizeValue = dn.classification?.totalSizeValue ?? null
                const capacityState = route?.vehicle ? deriveCapacityState(dnSizeValue, route.vehicle.sizeClass) : "DEFAULT"
                const isPickup = dn.classification?.deliveryChannel === "CUSTOMER_PICKUP"
                const driver = route?.driver ?? dn.staff ?? null
                const driverName = driver && (driver.lastName || driver.firstName) ? renderFullName(driver) : ""
                const phone = route?.driver?.phone ?? dn.staff?.phone ?? null
                const address = formatReceiverAddress(dn.receiverBranch, dn.receiverWarehouse)
                const subject = { deliveryNoteStatus: dn.deliveryNoteStatus, receiverPhone: phone }
                const lines = dn.lines?.length ?? 0
                const customerName = renderBusinessName(dn.receiverBusiness, "-")
                // The branch line repeats the business name for single-branch customers; show it only when it adds something.
                const branchName = dn.receiverBranch?.name && dn.receiverBranch.name !== customerName ? dn.receiverBranch.name : null
                // Channel (was its own column) and a non-regular order type ride on the number's second line.
                const numberMeta = [
                  t(`badges.channel.${dn.classification?.deliveryChannel ?? "unknown"}`),
                  dn.orderType && dn.orderType !== "REGULAR" ? t(`badges.orderType.${dn.orderType}`) : null,
                  route ? t("manage.active.stop", { route: route.routeName || route.routeNumber, n: route.stopNumber, total: route.totalStops }) : null,
                ].filter(Boolean).join(" · ")
                const itemsText = lines > 0
                  ? t("manage.pending.loadCount", { lines, qty: dn.totalItemCount ?? 0 })
                  : t("manage.active.qty", { qty: dn.totalItemCount ?? 0 })
                const timeText = sla.remainingMs <= 0
                  ? t("manage.sla.overdue")
                  : t("manage.active.dueAt", { at: dayjs(effectiveDate(dn) ?? undefined).format("MM-DD HH:mm") })

                return (
                  <Table.Tr key={id || dn.refCode} className={classes.row} onClick={() => id && navigate(`/supplier-delivery/detail/${id}`)}>
                    <Table.Td onClick={(e) => e.stopPropagation()}>
                      <Group gap={4} wrap="nowrap">
                        <DeliveryRowAction
                          label={t("active.detail")}
                          icon={<EyeIcon />}
                          state={stateOf("detail", subject)}
                          onClick={() => id && navigate(`/supplier-delivery/detail/${id}`)}
                        />
                        <DeliveryRowAction
                          label={t("active.copyTrackLink")}
                          icon={<LinkSimpleIcon />}
                          state={stateOf("trackLink", subject)}
                          onClick={() => id && handleCopyTrackLink(id)}
                        />
                        <DeliveryRowAction
                          label={phone ? t("active.callPhone", { phone }) : t("active.call")}
                          icon={<PhoneIcon />}
                          state={stateOf("callReceiver", subject)}
                          onClick={() => phone && window.open(`tel:${phone}`)}
                        />
                        {/* O111: the failed-attempt dialog was never reachable; it belongs to an active DN. */}
                        <DeliveryRowAction
                          label={t("failed.reportTitle")}
                          icon={<WarningIcon />}
                          state={stateOf("reportFailed", subject)}
                          onClick={() => setReportDn(dn)}
                        />
                      </Group>
                    </Table.Td>

                    <Table.Td>
                      <Text ff="monospace" fw={650} fz="var(--nw-fz-mono)" className={classes.one} translate="no">
                        {dn.refCode ?? "-"}
                      </Text>
                      <Text fz="var(--nw-fz-body)" c="var(--nw-text-2)" className={classes.one} title={`${numberMeta} · ${itemsText}`}>
                        {numberMeta}
                        <Text span inherit className={classes.itemsInline}>
                          {" · "}
                          {itemsText}
                        </Text>
                      </Text>
                    </Table.Td>

                    <Table.Td>
                      <Text fw={600} fz="var(--nw-fz-body)" className={classes.one} title={branchName ? `${customerName} · ${branchName}` : customerName}>
                        {customerName}
                        {branchName && (
                          <Text span inherit fw={400} c="var(--nw-text-2)">
                            {" · "}
                            {branchName}
                          </Text>
                        )}
                      </Text>
                      {address && (
                        <Text fz="var(--nw-fz-body)" c="var(--nw-text-2)" className={classes.one} title={address}>
                          {address}
                        </Text>
                      )}
                    </Table.Td>

                    <Table.Td className={classes.colItems}>
                      <Text fz="var(--nw-fz-body)" className={classes.one} title={itemsText}>
                        {itemsText}
                      </Text>
                      {dnSizeValue && (
                        <Text fz="var(--nw-fz-body)" c="var(--nw-text-2)" className={classes.one}>
                          {t("active.sizeBadge", { value: dnSizeValue })}
                        </Text>
                      )}
                    </Table.Td>

                    <Table.Td>
                      {isPickup ? (
                        <Text fz="var(--nw-fz-body)" c="var(--nw-text-2)">
                          {t("manage.active.noVehicleNeeded")}
                        </Text>
                      ) : route?.vehicle ? (
                        <VehicleCell route={route} dnSize={dnSizeValue} />
                      ) : (
                        <Text fz="var(--nw-fz-body)" c="var(--nw-text-2)">
                          {capacityState === "DANGER" ? t("active.needBiggerVehicle") : "-"}
                        </Text>
                      )}
                    </Table.Td>

                    <Table.Td>
                      {isPickup ? (
                        <Text fz="var(--nw-fz-body)" c="var(--nw-text-2)">
                          {t("manage.active.noDriverNeeded")}
                        </Text>
                      ) : driverName ? (
                        <>
                          <Text fz="var(--nw-fz-body)" className={classes.one} title={driverName}>
                            {driverName}
                          </Text>
                          {driver?.phone && (
                            <Text ff="monospace" fz="var(--nw-fz-mono)" c="var(--nw-text-2)" className={classes.one}>
                              {driver.phone}
                            </Text>
                          )}
                        </>
                      ) : (
                        <Text fz="var(--nw-fz-body)" c="var(--nw-text-2)">
                          {t("manage.active.notAssigned")}
                        </Text>
                      )}
                    </Table.Td>

                    <Table.Td>
                      {isPickup ? (
                        <Text fz="var(--nw-fz-body)" c="var(--nw-text-2)">
                          {t("manage.active.pickup")}
                        </Text>
                      ) : (
                        <GpsStatusCell
                          isLive={isLiveDn(dn, liveWindow)}
                          lastSeenLabel={lastSeen(dn)}
                          eta={effectiveDate(dn) ? dayjs(effectiveDate(dn)).format("HH:mm") : null}
                        />
                      )}
                    </Table.Td>

                    <Table.Td className={classes.colStatus}>
                      <DnStatusBadge status={dn.deliveryNoteStatus} />
                    </Table.Td>

                    <Table.Td>
                      <Group gap={6} wrap="nowrap">
                        <Dot tone={PRIORITY_TONE[priority]} />
                        <Text fw={600} fz="var(--nw-fz-body)">
                          {t(`manage.priority.${priority}`)}
                        </Text>
                      </Group>
                      <Text fz="var(--nw-fz-body)" c="var(--nw-text-2)" className={classes.one} title={timeText}>
                        {timeText}
                      </Text>
                      {(dn.failedAttemptsCount ?? 0) > 0 && (
                        <Text fz="var(--nw-fz-body)" c="var(--nw-text-2)">
                          {t("active.attemptN", { n: dn.failedAttemptsCount })}
                        </Text>
                      )}
                    </Table.Td>
                  </Table.Tr>
                )
              })}
            </Table.Tbody>
          )}
          footer={(p) => (
            <FrameFooter>
              <TablePager page={p.page} pageSize={p.limit} total={p.total} onPageChange={p.setPage} onPageSizeChange={p.setLimit} />
            </FrameFooter>
          )}
        />
      </Box>

      {reportDn?.id && (
        <ReportFailedDialog
          opened
          deliveryNoteId={reportDn.id}
          deliveryNoteRef={reportDn.refCode ?? null}
          onClose={() => setReportDn(null)}
          onReported={() => {
            refreshStats()
            tableRef.current?.reload()
          }}
        />
      )}
    </Stack>
  )
}
